import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportPayload {
  reportName: string;
  params: Record<string, any>;
  columns: string[];
  rows: Record<string, any>[];
  runAt: string;
  summaryCards?: Array<{ label: string; value: string }>;
}

// ── Excel export ─────────────────────────────────────────────────────────────

export async function exportToExcel(payload: ExportPayload): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LAOSS Reports';
  wb.created = new Date();

  const ws = wb.addWorksheet(payload.reportName.slice(0, 31) || 'Report');

  ws.mergeCells('A1:D1');
  ws.getCell('A1').value = 'LAOSS — ' + payload.reportName;
  ws.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF1A1714' } };

  ws.getCell('A2').value = 'Run at:';
  ws.getCell('B2').value = new Date(payload.runAt).toLocaleString();
  ws.getCell('A3').value = 'Date range:';
  ws.getCell('B3').value = `${payload.params.dateFrom ?? ''} – ${payload.params.dateTo ?? ''}`;

  let headerRows = 4;
  if (payload.summaryCards?.length) {
    payload.summaryCards.forEach((card, i) => {
      ws.getCell(`A${4 + i}`).value = card.label;
      ws.getCell(`B${4 + i}`).value = card.value;
    });
    headerRows = 4 + payload.summaryCards.length + 1;
  }

  const headerRow = ws.getRow(headerRows);
  payload.columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF9F27' } };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFBA7517' } } };
  });
  headerRow.commit();

  payload.rows.forEach((row, ri) => {
    const wsRow = ws.getRow(headerRows + 1 + ri);
    payload.columns.forEach((col, ci) => {
      const cell = wsRow.getCell(ci + 1);
      cell.value = row[col] ?? '';
      if (ri % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8F0' } };
      }
      if (isCurrencyColumn(col)) {
        cell.numFmt = '$#,##0.00';
        cell.alignment = { horizontal: 'right' };
      } else if (isAgeColumn(col) || isPercentColumn(col)) {
        cell.alignment = { horizontal: 'right' };
      }
    });
    wsRow.commit();
  });

  const sumRow = ws.getRow(headerRows + payload.rows.length + 2);
  sumRow.getCell(1).value = `Total rows: ${payload.rows.length}`;
  sumRow.getCell(1).font = { italic: true, color: { argb: 'FF888780' } };

  ws.columns.forEach(col => {
    col.width = Math.min(40, Math.max(12, col.width ?? 14));
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  downloadBlob(blob, sanitizeFilename(payload.reportName) + '.xlsx');
}

// ── PDF export ─────────────────────────────────────────────────────────────

export function exportToPdf(payload: ExportPayload): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  doc.setFontSize(14);
  doc.setTextColor(26, 23, 20);
  doc.text('LAOSS — ' + payload.reportName, 14, 16);

  doc.setFontSize(9);
  doc.setTextColor(136, 135, 128);
  doc.text(`Run: ${new Date(payload.runAt).toLocaleString()}`, 14, 22);
  doc.text(`Date range: ${payload.params.dateFrom ?? ''} – ${payload.params.dateTo ?? ''}`, 14, 27);

  if (payload.summaryCards?.length) {
    const cardStr = payload.summaryCards.map(c => `${c.label}: ${c.value}`).join('   |   ');
    doc.setTextColor(133, 79, 11);
    doc.text(cardStr, 14, 33);
  }

  autoTable(doc, {
    startY: payload.summaryCards?.length ? 38 : 32,
    head: [payload.columns],
    body: payload.rows.map(row => payload.columns.map(col => formatCellForPdf(col, row[col]))),
    styles: {
      fontSize: 7,
      cellPadding: 2,
      textColor: [68, 68, 65],
      lineColor: [232, 228, 222],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [239, 159, 39],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
    },
    alternateRowStyles: { fillColor: [255, 251, 244] },
    columnStyles: buildColumnStyles(payload.columns),
    didDrawPage: () => {
      doc.setFontSize(7);
      doc.setTextColor(180, 178, 169);
      doc.text(
        `Confidential — LAOSS Internal Use Only   |   Page ${doc.getCurrentPageInfo().pageNumber}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 8,
        { align: 'center' },
      );
    },
  });

  doc.save(sanitizeFilename(payload.reportName) + '.pdf');
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const AGE_KEYS = ['days', 'age'];

function isAgeColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return AGE_KEYS.some(k => lower.includes(k));
}

function isCurrencyColumn(col: string): boolean {
  const lower = col.toLowerCase();
  if (isAgeColumn(col)) return false;
  return ['balance', 'payment', 'amount', 'charge', 'adjustment', 'billed', 'collected', 'total'].some(k => lower.includes(k));
}

function isPercentColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return ['%', 'rate', 'percent'].some(k => lower.includes(k));
}

function formatCellForPdf(col: string, val: any): string {
  if (val == null) return '';
  if (isCurrencyColumn(col) && typeof val === 'number') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  }
  if (isPercentColumn(col) && typeof val === 'number') {
    return `${val.toFixed(1)}%`;
  }
  return String(val);
}

function buildColumnStyles(columns: string[]): Record<number, any> {
  const styles: Record<number, any> = {};
  columns.forEach((col, i) => {
    if (isCurrencyColumn(col) || isAgeColumn(col) || isPercentColumn(col)) {
      styles[i] = { halign: 'right' };
    }
  });
  return styles;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9_\- ]/g, '').trim().replace(/\s+/g, '_') || 'report';
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
